import { useEffect, useState } from "react";
import { Check, X, Trash2, Plus, Star, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useDragReorder from "@/lib/useDragReorder";

const empty = { name: "", role: "", company: "", content: "", rating: 5 };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/testimonials", { params: { page: 1, page_size: 200 } }).then((r) => setItems(Array.isArray(r.data?.items) ? r.data.items : []));
  useEffect(() => { load(); }, []);

  const approve = async (id, val) => { await api.patch(`/admin/testimonials/${id}/approve?approve=${val}`); toast.success(val ? "Approved" : "Hidden"); load(); };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); };
  const openNew = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => { setEditingId(t.id); setForm({ name: t.name || "", role: t.role || "", company: t.company || "", content: t.content || "", rating: t.rating || 5 }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, rating: Number(form.rating) };
    if (editingId) {
      await api.put(`/admin/testimonials/${editingId}`, payload);
      toast.success("Updated");
    } else {
      await api.post("/admin/testimonials", payload);
      toast.success("Created");
    }
    setOpen(false); setEditingId(null); setForm(empty); load();
  };

  const drag = useDragReorder({
    items,
    getId: (t) => t.id,
    onReorder: async (newList, ids) => {
      setItems(newList);
      try { await api.post("/admin/testimonials/reorder", { ids }); toast.success("Order updated"); }
      catch { toast.error("Failed to save order"); load(); }
    },
  });

  return (
    <div className="space-y-6" data-testid="admin-testimonials">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Testimonials</h1>
          <p className="text-sm text-slate-500">Drag to reorder. Approve, hide, or add testimonials.</p>
        </div>
        <Button onClick={openNew} className="bg-primary hover:bg-primary/90" data-testid="testimonial-new"><Plus className="mr-1 h-4 w-4" />New</Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <ul className="divide-y">
          {items.map((t) => (
            <li
              key={t.id}
              {...drag.rowProps(t)}
              className={`flex items-start justify-between gap-4 p-4 transition-colors ${drag.rowClass(t) || "hover:bg-slate-50"}`}
              data-testid={`testimonial-row-${t.id}`}
            >
              <div className="flex min-w-0 flex-1 items-start gap-2">
                <div className="pt-0.5"><drag.Handle testId={`testimonial-row-${t.id}-handle`} /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 text-secondary">
                      {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-secondary" />)}
                    </div>
                    <Badge className={t.is_approved ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}>
                      {t.is_approved ? "Published" : "Pending"}
                    </Badge>
                  </div>
                  <p className="mt-2 text-sm text-slate-700 line-clamp-2">&ldquo;{t.content}&rdquo;</p>
                  <div className="mt-1.5 text-xs text-slate-500"><b>{t.name}</b>{t.role && ` · ${t.role}`}{t.company && ` · ${t.company}`}</div>
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => openEdit(t)} data-testid={`testimonial-edit-${t.id}`}><Pencil className="mr-1 h-3 w-3" />Edit</Button>
                {t.is_approved ? (
                  <Button variant="outline" size="sm" onClick={() => approve(t.id, false)} data-testid={`testimonial-hide-${t.id}`}><X className="mr-1 h-3 w-3" />Hide</Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => approve(t.id, true)} data-testid={`testimonial-approve-${t.id}`}><Check className="mr-1 h-3 w-3" />Approve</Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => remove(t.id)} data-testid={`testimonial-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
              </div>
            </li>
          ))}
          {items.length === 0 && <li className="p-10 text-center text-slate-400">No testimonials yet.</li>}
        </ul>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Edit testimonial" : "New testimonial"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="testimonial-form">
            <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="testimonial-form-name" /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Role</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="testimonial-form-role" /></div>
              <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="testimonial-form-company" /></div>
            </div>
            <div><Label>Content</Label><Textarea required rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="testimonial-form-content" /></div>
            <div><Label>Rating (1-5)</Label><Input type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="testimonial-form-rating" /></div>
            <DialogFooter><Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="testimonial-form-submit">{editingId ? "Save changes" : "Create"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
